import React from 'react';
import { MerchantInvoice, MerchantBillingSettings } from '../../../types/gst';

interface Props {
  invoice: MerchantInvoice;
  settings?: MerchantBillingSettings;
}

export const ClassicGstTemplate: React.FC<Props> = ({ invoice, settings }) => {
  const sellerName = invoice.sellerTradeName || invoice.sellerLegalName || settings?.companyName || 'Business Enterprise';
  const sellerGstin = invoice.sellerGstin || settings?.gstin || '';
  const sellerAddress = invoice.sellerAddress || settings?.address || '';
  const sellerPhone = invoice.sellerPhone || settings?.phone || '';
  const sellerEmail = invoice.sellerEmail || settings?.email || '';

  const bankName = settings?.bankName || 'HDFC Bank Ltd';
  const accountNumber = settings?.accountNumber || '50200088991122';
  const ifsc = settings?.ifscCode || 'HDFC0000240';
  const upiId = settings?.upiId || 'merchant@upi';

  return (
    <div className="bg-white text-slate-800 p-8 font-sans max-w-4xl mx-auto border border-slate-300 shadow-sm rounded-lg text-xs print:p-0 print:border-none print:shadow-none">
      {/* Header Banner */}
      <div className="border-b-2 border-indigo-600 pb-4 mb-4 flex justify-between items-start">
        <div className="space-y-1">
          <h1 className="text-xl font-bold text-indigo-900 tracking-tight uppercase">{sellerName}</h1>
          <p className="text-slate-600 max-w-md leading-relaxed">{sellerAddress}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-700 font-medium pt-1">
            {sellerGstin && <span><strong className="text-slate-900">GSTIN:</strong> {sellerGstin}</span>}
            {sellerPhone && <span><strong className="text-slate-900">Phone:</strong> {sellerPhone}</span>}
            {sellerEmail && <span><strong className="text-slate-900">Email:</strong> {sellerEmail}</span>}
          </div>
        </div>

        <div className="text-right space-y-1">
          <div className="inline-block bg-indigo-50 border border-indigo-200 text-indigo-800 font-bold px-3 py-1 rounded text-sm uppercase tracking-wider">
            TAX INVOICE
          </div>
          <div className="font-mono text-xs pt-1">
            <div><strong className="text-slate-900">Invoice No:</strong> {invoice.invoiceNumber}</div>
            <div><strong className="text-slate-900">Invoice Date:</strong> {invoice.invoiceDate}</div>
            {invoice.dueDate && <div><strong className="text-slate-900">Due Date:</strong> {invoice.dueDate}</div>}
            <div><strong className="text-slate-900">Place of Supply:</strong> {invoice.placeOfSupply}</div>
          </div>
        </div>
      </div>

      {/* Bill To & Ship To */}
      <div className="grid grid-cols-2 gap-4 border border-slate-200 rounded p-3 mb-4 bg-slate-50/50">
        <div>
          <div className="text-[10px] uppercase font-bold text-indigo-700 tracking-wider">Billed To (Customer):</div>
          <div className="font-bold text-slate-900 text-sm mt-0.5">{invoice.customerName}</div>
          <div className="text-slate-600 mt-0.5 leading-snug">{invoice.customerBillingAddress || 'Address not provided'}</div>
          {invoice.customerGstin && (
            <div className="mt-1 font-mono font-semibold text-slate-800">
              GSTIN: <span className="text-indigo-900">{invoice.customerGstin}</span>
            </div>
          )}
          {invoice.customerPhone && <div className="text-slate-600">Phone: {invoice.customerPhone}</div>}
        </div>

        <div>
          <div className="text-[10px] uppercase font-bold text-indigo-700 tracking-wider">Shipped To / Delivery:</div>
          <div className="text-slate-700 mt-0.5 leading-snug">
            {invoice.customerShippingAddress || invoice.customerBillingAddress || 'Same as billing address'}
          </div>
          <div className="mt-2 text-[11px] text-slate-600">
            <div><strong>Supply Type:</strong> {invoice.isInterState ? 'Inter-State (IGST)' : 'Intra-State (CGST + SGST)'}</div>
            {invoice.eInvoice?.irn && (
              <div className="font-mono text-[9px] text-slate-500 truncate" title={invoice.eInvoice.irn}>
                <strong>IRN:</strong> {invoice.eInvoice.irn.substring(0, 24)}...
              </div>
            )}
            {invoice.eWayBill?.ewbNumber && (
              <div className="font-mono text-[10px] text-slate-700">
                <strong>E-Way Bill:</strong> {invoice.eWayBill.ewbNumber}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Line Items Table */}
      <table className="w-full text-left border-collapse border border-slate-200 mb-4">
        <thead>
          <tr className="bg-indigo-900 text-white text-[10px] uppercase tracking-wider">
            <th className="p-2 border border-indigo-900 w-8 text-center">#</th>
            <th className="p-2 border border-indigo-900">Item Description</th>
            <th className="p-2 border border-indigo-900 text-center">HSN/SAC</th>
            <th className="p-2 border border-indigo-900 text-right">Qty</th>
            <th className="p-2 border border-indigo-900 text-right">Rate (₹)</th>
            <th className="p-2 border border-indigo-900 text-right">Taxable (₹)</th>
            <th className="p-2 border border-indigo-900 text-right">GST %</th>
            <th className="p-2 border border-indigo-900 text-right">Total (₹)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {invoice.lineItems.map((item, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'}>
              <td className="p-2 text-center text-slate-500 font-mono">{idx + 1}</td>
              <td className="p-2 font-medium text-slate-800">{item.description}</td>
              <td className="p-2 text-center font-mono text-slate-600">{item.hsnSac || '—'}</td>
              <td className="p-2 text-right font-mono">{item.quantity} {item.unit || ''}</td>
              <td className="p-2 text-right font-mono">₹{item.unitPrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              <td className="p-2 text-right font-mono font-medium">₹{item.taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              <td className="p-2 text-right font-mono text-indigo-700 font-semibold">{item.gstRate}%</td>
              <td className="p-2 text-right font-mono font-bold text-slate-900">₹{item.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Tax Breakdown & Grand Total */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Bank & Payment Info */}
        <div className="border border-slate-200 rounded p-3 bg-slate-50 text-[11px] space-y-1">
          <div className="font-bold text-indigo-900 text-xs mb-1">Bank Payment Details:</div>
          <div><strong>Bank Name:</strong> {bankName}</div>
          <div><strong>Account No:</strong> <span className="font-mono font-bold">{accountNumber}</span></div>
          <div><strong>IFSC Code:</strong> <span className="font-mono font-bold">{ifsc}</span></div>
          <div><strong>UPI ID:</strong> <span className="font-mono text-indigo-700 font-bold">{upiId}</span></div>
        </div>

        {/* Calculation Table */}
        <div className="border border-slate-200 rounded p-3 space-y-1 text-slate-700">
          <div className="flex justify-between py-0.5">
            <span>Taxable Subtotal:</span>
            <span className="font-mono font-medium">₹{invoice.subtotalTaxable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>

          {invoice.isInterState ? (
            <div className="flex justify-between py-0.5 text-indigo-900">
              <span>Integrated Tax (IGST):</span>
              <span className="font-mono font-medium">₹{invoice.totalIgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          ) : (
            <>
              <div className="flex justify-between py-0.5">
                <span>Central Tax (CGST):</span>
                <span className="font-mono font-medium">₹{invoice.totalCgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between py-0.5">
                <span>State Tax (SGST):</span>
                <span className="font-mono font-medium">₹{invoice.totalSgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </>
          )}

          {invoice.totalCess > 0 && (
            <div className="flex justify-between py-0.5">
              <span>Cess:</span>
              <span className="font-mono">₹{invoice.totalCess.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          )}

          {invoice.roundOff !== 0 && (
            <div className="flex justify-between py-0.5 text-slate-500">
              <span>Round Off:</span>
              <span className="font-mono">₹{invoice.roundOff.toFixed(2)}</span>
            </div>
          )}

          <div className="border-t border-slate-300 pt-1.5 flex justify-between font-bold text-sm text-indigo-950">
            <span>Grand Total:</span>
            <span className="font-mono text-base text-indigo-900">₹{invoice.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>

          {invoice.balanceDue > 0 ? (
            <div className="flex justify-between text-rose-700 font-semibold pt-1">
              <span>Balance Due:</span>
              <span className="font-mono">₹{invoice.balanceDue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          ) : (
            <div className="text-emerald-700 font-bold text-right pt-1">PAID IN FULL ✅</div>
          )}
        </div>
      </div>

      {/* Terms & Authorized Signature */}
      <div className="grid grid-cols-2 gap-4 border-t border-slate-200 pt-3 text-[10px] text-slate-500">
        <div>
          <div className="font-bold text-slate-700 uppercase tracking-wider mb-1">Terms & Conditions:</div>
          <p className="whitespace-pre-line leading-relaxed">{invoice.termsAndConditions || settings?.defaultTerms || 'Subject to local jurisdiction.'}</p>
        </div>
        <div className="text-right flex flex-col justify-between items-end">
          <div className="font-semibold text-slate-700">For {sellerName}</div>
          <div className="h-12 border-b border-dashed border-slate-400 w-36 mb-1"></div>
          <div className="text-slate-500 uppercase font-bold text-[9px] tracking-wider">Authorized Signatory</div>
        </div>
      </div>
    </div>
  );
};
